// Single source of truth for open Idukki roles. Drives both the
// /careers index and the per-role /careers/[slug] detail pages. Each
// role has enough copy for a credible job description plus a clean
// applyEmail target so the Apply form can fire a structured mailto.

export type RoleTeam =
  | 'Engineering'
  | 'AI/ML'
  | 'Design'
  | 'Sales'
  | 'Customer Success'
  | 'Marketing'
  | 'Partnerships'
  | 'Operations'

export type RoleLevel = 'Founding' | 'Staff' | 'Senior' | 'Lead' | 'Mid' | 'Junior' | 'Intern'

export type Role = {
  slug: string
  title: string
  team: RoleTeam
  level: RoleLevel
  /** Short location label, e.g. "Remote (UTC ±4)". */
  location: string
  /** Full-time / Contract / Internship */
  type: 'Full-time' | 'Contract' | 'Internship'
  /** Compensation tag for the index card, e.g. "£85K + 1.5× OTE". */
  compensation?: string
  /** One-paragraph elevator-pitch summary. */
  summary: string
  /** 4-6 chip-style highlights for the index card. */
  highlights: string[]
  /** Long-form sections rendered on the detail page. */
  about: string
  whatYoullDo: string[]
  whatYoullNeed: string[]
  niceToHaves?: string[]
  compensationDetails?: string[]
  interviewProcess?: string[]
}

export const careersEmail = 'careers@idukki.io'

const COMMON_INTERVIEW = [
  '30-min intro with a hiring manager (this week)',
  '60-min role-deep-dive with two team members',
  'Take-home or working session, paid if > 2 hours',
  '60-min founders chat',
  'Offer in 14 days or less from the intro call',
]

// Lighter loop for the CEO-office internship: we hire interns on judgment
// and hunger, not on a take-home gauntlet.
const INTERN_INTERVIEW = [
  '20-min intro call with the hiring lead (this week)',
  '45-min working chat: a real task from the founder’s desk, talked through live',
  '30-min meet-the-team coffee at the Delhi office',
  'Offer in 7 days or less from the intro call',
]

export const roles: Role[] = [
  {
    slug: 'senior-frontend-engineer-widget-runtime',
    title: 'Senior Frontend Engineer (Widget Runtime)',
    team: 'Engineering',
    level: 'Senior',
    location: 'Remote (UTC ±4)',
    type: 'Full-time',
    compensation: '£35.5K',
    summary: 'Own the 37 KB widget runtime that loads on storefronts across the web. Performance budget is your DRI metric.',
    highlights: ['React 19, TypeScript, Vite', 'CLS / INP / LCP budgets', 'A11y + i18n at scale', '£35.5K base'],
    about: 'The Idukki widget runtime is the single biggest piece of code in our customer-facing surface area. It runs on every PDP, PLP, lookbook, story rail and email tile across thousands of brand storefronts. You own its weight, its rendering performance, its accessibility, and the API the rest of the team builds on top of.',
    whatYoullDo: [
      'Hold the line on the 37 KB gzipped budget, every new feature gets weighed against impressions',
      'Drive Core Web Vitals on every customer site: keep CLS < 0.001, INP < 200ms, LCP impact < 50ms',
      'Build the public widget SDK + storybook + visual-regression harness that the AI/ML and product teams ship against',
      'Own A11y conformance (WCAG 2.2 AA) and i18n across 10 European + 4 APAC locales',
      'Run our experimentation framework: variant injection, sticky bucketing, server-side fallback',
      'Mentor 2-3 engineers; participate in hiring loops',
    ],
    whatYoullNeed: [
      '6+ years shipping production JavaScript/TypeScript with a deep React + Web Components background',
      'Demonstrable runtime-performance wins on a high-traffic surface (case study or shipped repo)',
      'Hands-on with Vite, esbuild, rollup tree-shaking + module-federation tradeoffs',
      'Has owned an a11y conformance audit end-to-end',
      'Comfort working async with a globally distributed team',
    ],
    niceToHaves: [
      'Shopify theme + Liquid + checkout-extensibility familiarity',
      'Prior work on a CDN-distributed JS SDK',
      'Experience with edge-rendered React (Cloudflare Workers, Fastly Compute)',
    ],
    compensationDetails: [
      'Base: £32K – £39.5K depending on level',
      'Healthcare, $2K home-office, $1.5K learning budget, 25 days holiday + local public holidays',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'staff-backend-engineer-ingestion-platform',
    title: 'Staff Backend Engineer (Ingestion Platform)',
    team: 'Engineering',
    level: 'Staff',
    location: 'Remote (Europe)',
    type: 'Full-time',
    compensation: '€50.5K',
    summary: 'Scale the ingestion pipeline that pulls 12M+ social posts/day across 13 sources. Postgres, Kafka, ClickHouse.',
    highlights: ['High-throughput ETL', 'Postgres + ClickHouse', 'Owns SLOs', '€50.5K base'],
    about: 'The Idukki ingestion platform is how every Instagram post, TikTok clip, X mention and YouTube Short reaches a customer dashboard. You own the pipeline end-to-end: connector lifecycle, rate-limit handling, dedupe, enrichment, durability, and the SLOs we publish to customers.',
    whatYoullDo: [
      'Own ingestion SLOs: < 60s p95 from post → dashboard, 99.99% durability, < 0.1% dedupe miss-rate',
      'Scale the platform from 12M to 40M posts/day without a re-architecture',
      'Lead 3-engineer squad on the connector lifecycle (rate-limit drift, deprecations, capacity)',
      'Partner with AI/ML on the enrichment pipeline, auto-tagging, brand-safety, embeddings',
      'Run incident command for ingestion-side P0s; write the post-mortem',
    ],
    whatYoullNeed: [
      '8+ years backend / distributed systems with at least one stint owning a > 1B-event/day pipeline',
      'Postgres tuning at scale (partitioning, logical replication, vacuum strategies)',
      'ClickHouse or another columnar warehouse in production',
      'Kafka / Kinesis / similar, you can talk about exactly-once delivery and back-pressure with conviction',
      'Demonstrable on-call leadership',
    ],
    compensationDetails: [
      'Base: €45K – €60K depending on level',
      'Same healthcare + home-office + learning + holiday package as engineering',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'founding-ai-ml-engineer',
    title: 'Founding AI/ML Engineer',
    team: 'AI/ML',
    level: 'Founding',
    location: 'Remote (UTC ±6)',
    type: 'Full-time',
    compensation: 'Founding equity',
    summary: 'Own auto-tagging, content moderation, video understanding. Ship the AI pipeline behind every Idukki gallery.',
    highlights: ['CLIP, vision transformers', 'Inference at $0.001/post', 'Direct line to founders'],
    about: 'We process millions of UGC pieces a day. You own the model layer that decides what they\'re about, whether they\'re brand-safe, which products they show, and how they should be ranked. Founding role, you build the team you want around you.',
    whatYoullDo: [
      'Own the auto-tagging two-pass pipeline (Claude Vision detect + recognise) and replace it with our own model where the unit economics work',
      'Ship the six-axis brand-safety scorer ahead of every render that hits a paid channel',
      'Drive the agentic-shopping ranker, visual + textual + behavioural signals into one PDP recommendation',
      'Build the AI eval harness so model regressions get caught before they reach customers',
      'Hire 3-4 engineers in year one',
    ],
    whatYoullNeed: [
      '7+ years ML engineering shipping production models on a high-traffic surface',
      'CLIP-family + vision-transformer experience in production',
      'Cost-conscious inference architecture, you can hit $0.001 / post and justify the trade-offs',
      'Bias toward shipping over publishing',
    ],
    compensationDetails: [
      'Base: $67.5K – $82.5K depending on level',
      'Equity: 1.0% – 2.0% (four-year vest, one-year cliff), founding band',
      'Same global benefits package as engineering',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'founding-solutions-engineer',
    title: 'Founding Solutions Engineer',
    team: 'Sales',
    level: 'Founding',
    location: 'Remote (AMER + EMEA)',
    type: 'Full-time',
    compensation: '$60K base + 1.3× OTE',
    summary: 'Pair with AEs on every > $50K ARR deal. Run technical demos, integration spikes and proof-of-concept builds.',
    highlights: ['Pre-sales for ent. Shopify Plus', 'Klaviyo, GA4, Segment fluency', 'Lives in customer Slacks', '$60K + OTE'],
    about: 'You sit between Sales and Engineering. You own the technical conversation from first demo through go-live, including the integration spike, the security questionnaire and the post-launch optimisation. Founding band: you build the SE playbook and the team after you.',
    whatYoullDo: [
      'Run technical demos in every > $50K ARR deal',
      'Build the proof-of-concept integration on the prospect\'s staging environment in < 5 working days',
      'Own the security questionnaire and SOC 2 / DPA support together with our compliance lead',
      'Sit in the customer Slack for the first 30 days post-launch and unblock anything technical',
      'Document the patterns, you\'re writing the SE playbook we hire against',
    ],
    whatYoullNeed: [
      'Pre-sales SE background at a Shopify Plus / Klaviyo / Segment / similar SaaS company',
      'Hands-on with at least three of: Shopify Liquid, GA4 server-side, Meta CAPI, GTM, Segment, Klaviyo flows',
      'You\'ve led a security review with a > $1B retailer and won',
      'Strong written discovery skills, you can write a 1-page integration plan in 30 minutes',
    ],
    compensationDetails: [
      'Base: $52.5K – $67.5K',
      'OTE: 1.3× base (variable tied to deal-stage progression, not signed contracts)',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'account-executive-emea',
    title: 'Account Executive. EMEA',
    team: 'Sales',
    level: 'Senior',
    location: 'London or Remote (UK / DE)',
    type: 'Full-time',
    compensation: '£32K + 1.5× OTE',
    summary: 'Mid-market ecommerce. £1.5M ARR quota. Inbound-heavy pipeline, 3-call average close, 60-day average cycle.',
    highlights: ['Mid-market ecom track record', '£32K + 1.5× OTE', '3-call close motion'],
    about: 'You own the EMEA mid-market segment ($1M – $10M GMV brands). Inbound-heavy because the comparison pages do most of the qualification, but you\'ll run outbound when the territory needs it. The job is to close the brands who already chose Idukki vs. Bazaarvoice or Yotpo.',
    whatYoullDo: [
      'Carry a £1.5M ARR new-business quota',
      'Run 3-call close motion: discovery → SE-led demo → commercial. 60-day average cycle',
      'Partner with the SE on every > £25K ARR deal',
      'Feed the comparison-page editorial team with the objections you hear weekly',
    ],
    whatYoullNeed: [
      '5+ years closing mid-market ecommerce SaaS in EMEA',
      'Demonstrated quota attainment > 100% in the last 2 of 3 years',
      'Comfort negotiating against Bazaarvoice / Yotpo / Tolstoy renewal cycles',
      'Native or near-native English; a second European language is a strong plus',
    ],
    compensationDetails: [
      'Base: £32K',
      'OTE: £48K (1.5×)',
      'Same benefits package as the rest of the company',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'account-executive-amer',
    title: 'Account Executive. AMER',
    team: 'Sales',
    level: 'Senior',
    location: 'Remote (NYC + Bay)',
    type: 'Full-time',
    compensation: '$45K + 2× OTE',
    summary: 'East + West coast territory. Strong outbound motion expected. $45K base + 2× OTE.',
    highlights: ['East + West DTC focus', '$45K + 2× OTE', '~10% quarterly travel'],
    about: 'AMER is our newest market and you\'re hire #2 on the ground. Outbound-heavy in year one as we build the inbound funnel. Territory split: East and West coast DTC + mid-market retail.',
    whatYoullDo: [
      'Carry a $1.8M ARR new-business quota',
      'Run a balanced inbound + outbound motion (60/40 outbound in year one)',
      'Co-own the AMER playbook with the VP Sales as we localise the EMEA model',
      'Quarterly customer travel (~10%). NRF, Shoptalk, CommerceNext',
    ],
    whatYoullNeed: [
      '5+ years closing ecom SaaS in AMER',
      'Comfort with $50K – $250K ARR deals',
      'Strong outbound chops, you can run a 50-account list end-to-end',
      'Track record selling against incumbents (Bazaarvoice, Yotpo, Stamped)',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'sales-development-rep-emea',
    title: 'Sales Development Rep. EMEA',
    team: 'Sales',
    level: 'Mid',
    location: 'Remote (EMEA)',
    type: 'Full-time',
    compensation: '£16K + 1.4× OTE',
    summary: 'Outbound to mid-market Shopify Plus + BigCommerce. Apollo, Outreach, LinkedIn Sales Nav, Idukki theme detector.',
    highlights: ['350 meaningful touches / week', 'Path to AE in 18 months', 'Quota: 12 SQOs/month'],
    about: 'The EMEA SDR team is three people; you\'d be the fourth. We expect 12 sales-qualified opportunities a month. Tooling is good: Apollo + Outreach + LinkedIn Sales Nav + our internal theme-detector that tells you which Shopify Plus brands run Bazaarvoice / Yotpo today.',
    whatYoullDo: [
      'Hit 12 SQOs / month for one of two AEs',
      'Maintain a 350-touch-per-week cadence across LinkedIn, email, and call',
      'Use the Idukki theme detector to qualify the prospect\'s current stack pre-outreach',
      'Feed messaging tests back to the growth team, what subject lines / openings convert',
    ],
    whatYoullNeed: [
      '2-3 years SDR/BDR experience in ecommerce SaaS',
      'Demonstrated > 100% quota attainment in the last 2 quarters',
      'Strong written-tone calibration, you can sound right for both DTC founder + mid-market eCom director audiences',
      'Outreach / Apollo / SalesLoft fluency',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'sales-development-rep-amer',
    title: 'Sales Development Rep. AMER',
    team: 'Sales',
    level: 'Mid',
    location: 'Remote (US)',
    type: 'Full-time',
    compensation: '$26.5K + 1.4× OTE',
    summary: 'Outbound to DTC + Shopify Plus retail in AMER. Apollo, Outreach, Idukki theme detector, 12 SQOs / month.',
    highlights: ['350 meaningful touches / week', 'Path to AE in 18 months', 'Quota: 12 SQOs/month'],
    about: 'New AMER SDR seat, first hire on the ground supporting the AMER AE. Outbound-heavy because the AMER inbound funnel is still being built; you have the chance to shape the playbook from day one.',
    whatYoullDo: [
      'Hit 12 SQOs / month',
      'Build the AMER outbound playbook alongside the AE and Head of Growth',
      'Run cold-call experiments (we believe in cold-call, when the prep is good)',
      'Live in the customer Slack channels post-close for two weeks to soak up vocabulary',
    ],
    whatYoullNeed: [
      '2-3 years SDR experience selling into US ecommerce or martech',
      'Comfortable with first-on-the-ground ambiguity',
      'Demonstrated outbound cold-call win rate (we will ask for a recording you\'re proud of)',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'customer-success-manager-enterprise',
    title: 'Customer Success Manager. Enterprise',
    team: 'Customer Success',
    level: 'Senior',
    location: 'Remote (AMER)',
    type: 'Full-time',
    compensation: '$49K + bonus',
    summary: '18 strategic accounts > $75K ARR. Quarterly business reviews, executive sponsorship, expansion targets.',
    highlights: ['$3M ARR book', 'Net retention target: 118%', 'Owns expansion + renewal'],
    about: 'You own the post-sale lifecycle for our biggest accounts. 18-account book averaging $170K ARR. The bar is quarterly QBRs run from real data (not slides), 12-week onboarding plans, and a net retention target of 118%.',
    whatYoullDo: [
      'Own renewal + expansion across 18 strategic accounts',
      'Lead quarterly business reviews, every QBR includes a live A/B-test result and one new capability',
      'Partner with the SE on every renewal-blocking technical issue',
      'Identify expansion: signage, agentic shopping, rights API across the book',
    ],
    whatYoullNeed: [
      '5+ years CS at a SaaS company selling into ecommerce, martech, or retail tech',
      'Track record carrying a > $2M ARR book at 110%+ NRR',
      'Comfortable with C-suite + VP-level executives',
      'Will-tear-up-the-slide-deck-and-show-the-dashboard energy',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'customer-success-manager-mid-market',
    title: 'Customer Success Manager. Mid-market',
    team: 'Customer Success',
    level: 'Mid',
    location: 'Remote (EMEA)',
    type: 'Full-time',
    compensation: '€25.5K + bonus',
    summary: '40-account portfolio in the $10–$50K ARR band. Heavy onboarding + adoption focus, light renewal motion.',
    highlights: ['Onboarding playbook owner', 'Net retention: 105%+', 'Reports to Director, CS'],
    about: 'You own the high-velocity post-sale motion. 40 accounts, monthly check-ins, structured onboarding milestones, and a soft expansion role (you flag, the AE closes).',
    whatYoullDo: [
      '90-day onboarding for every new mid-market account, adoption is your DRI',
      'Run a weekly cohort review with the rest of the CS team',
      'Build and maintain the onboarding playbook the rest of the team uses',
      'Flag expansion signals to the AE; no quota, just visibility',
    ],
    whatYoullNeed: [
      '3+ years CS or onboarding in SaaS',
      'Bias to async + written process, playbooks > tribal knowledge',
      'Comfort with ambiguity in a smaller book of accounts',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'senior-product-designer',
    title: 'Senior Product Designer',
    team: 'Design',
    level: 'Senior',
    location: 'Remote (Europe)',
    type: 'Full-time',
    compensation: '€35.5K',
    summary: 'Own the dashboard surface area: galleries, rights workflow, analytics, A/B testing. Partner directly with PM + EM.',
    highlights: ['10 yrs SaaS dashboards', 'Owns Figma library', 'Ships motion + prototypes'],
    about: 'You own the design system + the dashboard experience. 6 squads run on Figma against your library. You ship motion specs, you write the IA, and you partner with engineering on the implementation.',
    whatYoullDo: [
      'Own + grow the Idukki design system + Figma library (currently ~140 components)',
      'Run the design crit; mentor 3 product designers',
      'Pair with engineering through the implementation pass, no throw-over-the-wall handoffs',
      'Ship 3 motion specs / quarter (Lottie or Rive)',
    ],
    whatYoullNeed: [
      '8+ years product design in B2B SaaS, at least 4 in a senior IC role',
      'A portfolio of dashboard work that handles real complexity (data tables, multi-step flows, role-based access)',
      'Strong systems thinking, your design system stays usable as it scales',
      'Comfortable shipping production code (or willing to learn the basics)',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'junior-graphic-designer',
    title: 'Junior Graphic Designer',
    team: 'Design',
    level: 'Junior',
    location: 'Remote (India)',
    type: 'Full-time',
    compensation: '₹6L CTC',
    summary: 'Your first design job, done right. Ship the marketing graphics, social tiles and deck slides the team needs daily, with a senior designer reviewing your work.',
    highlights: ['0-2 yrs experience', 'Figma + brand system', 'Ships weekly, reviewed daily', 'Path to Product Designer'],
    about: 'This is built for someone early in their design career who wants real reps, not a portfolio-building side project. You work inside the existing Idukki design system: social tiles, deck slides, one-off marketing graphics, and the small visual assets that content and sales ask for every week. A senior product designer reviews your work daily, so you learn the system instead of guessing at it.',
    whatYoullDo: [
      'Ship social graphics, one-pagers and deck slides against the Idukki Figma library',
      'Turn rough briefs from content, sales and partnerships into on-brand assets within a day',
      'Keep exports consistent: correct sizes, correct file types, correct naming, every time',
      'Sit in on the weekly design crit and take feedback on the same piece more than once without it stinging',
      'Build toward owning small end-to-end surfaces (a landing section, a comparison-page graphic) as you grow',
    ],
    whatYoullNeed: [
      '0-2 years of graphic design experience; recent design-course or bootcamp graduates welcome',
      'Comfortable in Figma and at least one raster/vector tool (Photoshop, Illustrator, or similar)',
      'A portfolio, even a student one, that shows layout sense and attention to detail',
      'Coachable, you can take a note and apply it without re-litigating it',
    ],
    niceToHaves: [
      'Some exposure to motion (Lottie, After Effects, or just curiosity about it)',
      'Prior work for an ecommerce or DTC brand, even informally',
    ],
    compensationDetails: [
      'CTC: ₹6L, depending on profile',
      'Direct mentorship from the Senior Product Designer and a documented path to Product Designer',
      'Same benefits package as the rest of design and engineering',
    ],
    interviewProcess: [
      '30-min intro with the hiring manager (this week)',
      'Take-home: spend time on idukki.io and come back with three graphics or page-design changes you would make and why, no more than 2 hours',
      '60-min portfolio + take-home walkthrough with the Senior Product Designer',
      '30-min founders chat',
      'Offer in 14 days or less from the intro call',
    ],
  },
  {
    slug: 'content-marketer-benchmarks-seo',
    title: 'Content Marketer. Benchmarks + SEO',
    team: 'Marketing',
    level: 'Senior',
    location: 'Remote (Anywhere)',
    type: 'Full-time',
    compensation: '$41.5K',
    summary: 'Write the comparison pages, benchmark reports and migration playbooks customers Google. Real analytics chops required.',
    highlights: ['SEO + technical writing', 'Original data > LLM slop', 'Owns 200+ comparison pages'],
    about: 'We compete by writing better than the category incumbents. You own the comparison wall, the benchmarks reports, and the migration playbooks. Real analytics chops required, you\'ll be working with customer data, not just keyword tools.',
    whatYoullDo: [
      'Maintain + grow the comparison wall (200+ pages, indexed)',
      'Ship a quarterly benchmarks report drawn from anonymised Idukki data',
      'Write the migration playbooks (Bazaarvoice → Idukki, Yotpo → Idukki, Tolstoy → Idukki)',
      'Own the technical SEO surface. Core Web Vitals, schema markup, internal linking',
    ],
    whatYoullNeed: [
      '5+ years SaaS content + SEO',
      'Has shipped at least one well-cited benchmarks / state-of report',
      'Comfort with SQL or a BI tool (Mode, Hex, Metabase)',
      'A writing sample we can actually read all the way through',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'social-media-manager',
    title: 'Social Media Manager. Founders\' Brand + Idukki',
    team: 'Marketing',
    level: 'Mid',
    location: 'Remote (Europe)',
    type: 'Full-time',
    compensation: '€25.5K',
    summary: 'Run the Idukki + founder accounts across LinkedIn, X, TikTok, Threads, Instagram. Ship 4-5 posts a day. Pull every benchmark + product launch into a thread.',
    highlights: ['Daily posting cadence', 'Founder + brand voice', 'Owns LinkedIn + TikTok', 'Direct line to founders'],
    about: 'Our content team writes long-form; you turn it into social. You also run the founder accounts (Rohin + Sahil): coaching them on cadence, drafting, and amplification. Comfortable being measured on impressions + qualified-traffic, not just likes.',
    whatYoullDo: [
      'Run @idukki accounts on LinkedIn, X, TikTok, Threads + Instagram. 4-5 posts a day across the portfolio',
      'Coach Rohin + Sahil on their personal accounts, draft, schedule, review',
      'Repurpose every comparison page, benchmark report and product launch into a thread + a short-form clip',
      'Own the post-mortem: weekly review of what landed, by impressions + qualified-traffic-to-/pricing',
      'Test new formats, long-form LinkedIn carousels, TikTok demo loops, Threads commentary',
    ],
    whatYoullNeed: [
      '4+ years running a B2B SaaS social account that the team uses to drive pipeline',
      'Has run a founder account end-to-end (draft, schedule, amplify)',
      'Strong post-mortem instinct, you can name the three reasons a post landed',
      'Comfort with on-camera scripting + light editing (CapCut, Descript)',
    ],
    niceToHaves: [
      'Existing personal account with > 5K LinkedIn or > 10K Twitter following',
      'Prior ecommerce or DTC brand experience',
    ],
    compensationDetails: [
      'Base: €23.5K – €29.5K',
      'Same benefits package as marketing',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'content-creator-short-form',
    title: 'Content Creator. Short-form Video',
    team: 'Marketing',
    level: 'Mid',
    location: 'Remote (Europe)',
    type: 'Full-time',
    compensation: '€25.5K',
    summary: 'Make the short-form clips that explain Idukki on TikTok, Reels and LinkedIn. On-camera, off-camera, scripting + editing. 3 publishable clips a week.',
    highlights: ['Camera + edit fluency', 'On-camera comfortable', '3 clips a week', 'CapCut / Descript / Premiere'],
    about: 'We need shorter, more concrete content than our blog can provide. You own the short-form pipeline: script, shoot (in studio or with customers), edit, ship. Three publishable clips a week is the bar.',
    whatYoullDo: [
      'Ship 3 short-form clips (15-90s) per week across TikTok, Reels, LinkedIn',
      'Script + shoot demo clips with the product team (we have a studio kit and a Sony FX3)',
      'Travel quarterly to customer sites for real-world clips',
      'Own the asset library + the clip-tagging convention',
    ],
    whatYoullNeed: [
      '3+ years short-form video for a brand or creator account',
      'Strong on-camera presence + comfortable scripting your own segments',
      'Fluent in CapCut + Descript + Premiere (at least two of the three)',
      'Portfolio of at least 20 clips you can talk through',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'partnerships-manager-agencies',
    title: 'Partnerships Manager. Agencies',
    team: 'Partnerships',
    level: 'Lead',
    location: 'Remote (NYC / LDN)',
    type: 'Full-time',
    compensation: '$45K + bonus',
    summary: 'Recruit + activate the agency partner program. Co-marketing with Shopify Plus, BigCommerce and 50+ regional agencies.',
    highlights: ['Owns the agency P&L', 'Hosts the quarterly summit', '15% of new ARR target'],
    about: 'Agencies should be 15% of new ARR by Q4. You build the program: tiering, certification, co-marketing, deal-reg, the quarterly summit. Reports to the VP Sales.',
    whatYoullDo: [
      'Recruit + onboard 50+ agency partners in year one',
      'Run the certification program (live training, exam, badge)',
      'Build the agency portal + deal-registration flow with engineering',
      'Host the quarterly Idukki Agency Summit (London + NYC alternating)',
      'Co-marketing, webinar series, joint case studies, conference co-sponsorship',
    ],
    whatYoullNeed: [
      '6+ years building agency partner programs at a SaaS company',
      'Has owned a partner-attributed-ARR target',
      'Comfort negotiating co-marketing budgets',
      'Network in Shopify Plus / BigCommerce / Klaviyo + agency ecosystems',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'office-intern-ceo-office',
    title: 'Office Intern. CEO’s Office',
    team: 'Operations',
    level: 'Intern',
    location: 'Delhi (Model Town), in-office',
    type: 'Internship',
    compensation: 'Monthly stipend + founder mentorship',
    summary: 'Work shoulder-to-shoulder with the founder out of the Delhi office. Own the calendar, the inbox triage, the follow-ups, and the small things that quietly run a startup.',
    highlights: ['Direct line to the founder', 'Delhi office, in-person', '3-6 month internship', 'Path to a full-time seat'],
    about: 'This is a chief-of-staff internship in miniature. You sit next to the founder, see how an early-stage company actually gets run, and take real work off his plate: scheduling, research, vendor coordination, travel, and the running list of loose ends that never make it into a job description. It suits someone early in their career who is organised, unflappable, and wants a front-row seat to how Idukki is built.',
    whatYoullDo: [
      'Own the founder’s calendar, meeting prep and inbox triage so the day runs on time',
      'Turn messy asks into done: book the travel, chase the vendor, draft the doc, close the loop',
      'Sit in on partner and customer calls, take the notes, then own the follow-ups',
      'Run quick research sprints (markets, hiring, tools) and come back with a one-pager, not a data dump',
      'Keep the Delhi office humming: supplies, visitors, the bits that keep a small team unblocked',
    ],
    whatYoullNeed: [
      'Recent graduate or final-year student who can be in the Delhi (Model Town) office most days',
      'Genuinely organised, you keep a list and things do not fall off it',
      'Clear written English and the confidence to chase people politely until something is done',
      'Discreet with sensitive information and comfortable around senior people',
      'Comfortable with Google Workspace (Docs, Sheets, Calendar) and quick to pick up new tools',
    ],
    niceToHaves: [
      'A side project, club, or event you ran end-to-end',
      'Interest in startups, sales, or product',
    ],
    compensationDetails: [
      'Competitive monthly stipend (confirmed at offer)',
      'Direct mentorship from the founder and exposure to every part of the business',
      'Strong interns are first in line for a full-time Operations or Sales seat',
    ],
    interviewProcess: INTERN_INTERVIEW,
  },
  {
    slug: 'junior-sdr-india',
    title: 'Junior Sales Development Rep. India',
    team: 'Sales',
    level: 'Junior',
    location: 'Delhi or Remote (India)',
    type: 'Full-time',
    compensation: '₹11.5L – ₹13.5L + 1.3× OTE',
    summary: 'Your first sales job, done right. Learn outbound to ecommerce brands with proper tooling, real coaching, and a clear path to senior SDR and then AE.',
    highlights: ['0-6 months experience', 'Full ramp + coaching', 'Apollo, Outreach, LinkedIn', 'Path to AE'],
    about: 'This is built for someone at the very start of their sales career, zero to six months in. You will not be thrown at a phone and told good luck. We pair you with a senior SDR, give you a 30-day ramp, and teach you the craft: how to research an ecommerce brand, write a message someone actually replies to, and book a meeting that holds. The tooling is good (Apollo, Outreach, LinkedIn Sales Nav, and our internal theme detector that tells you which Shopify brands run a competitor today).',
    whatYoullDo: [
      'Ramp through a structured 30-day onboarding on the product, the market and the outbound playbook',
      'Build prospect lists of Shopify and BigCommerce brands using the Idukki theme detector',
      'Send a daily cadence of personalised LinkedIn, email and call touches under a senior SDR’s wing',
      'Book qualified meetings for the India AE and learn to run a clean discovery handoff',
      'Log everything cleanly in the CRM, your pipeline hygiene is part of the job',
    ],
    whatYoullNeed: [
      '0-6 months of work experience; recent graduates strongly encouraged to apply',
      'Clear, friendly written English and a voice that is comfortable on a cold call',
      'Genuine curiosity about ecommerce, brands and why people buy',
      'Coachable, organised and resilient, you treat a no as data, not a wound',
    ],
    niceToHaves: [
      'A college internship, sales club, or side hustle where you sold something',
      'Familiarity with LinkedIn or any CRM',
    ],
    compensationDetails: [
      'Base: ₹11.5L – ₹13.5L depending on profile',
      'OTE: 1.3× base, tied to qualified meetings booked',
      'Full ramp, weekly coaching, and a documented path to Senior SDR in 12-18 months',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'senior-sdr-india',
    title: 'Senior Sales Development Rep. India',
    team: 'Sales',
    level: 'Senior',
    location: 'Delhi or Remote (India)',
    type: 'Full-time',
    compensation: '₹14.5L – ₹17.5L + 1.5× OTE',
    summary: 'Run outbound to India and APAC ecommerce brands with autonomy. Own your territory, hit 12 SQOs a month, and help build the playbook the junior reps learn from.',
    highlights: ['3-6 yrs SDR experience', 'Owns a territory', '12 SQOs / month', 'Mentors junior reps'],
    about: 'You have done outbound before and you are good at it. Now you want a seat where your judgment is trusted: your own territory, your own experiments, and a real say in how the team sells. You will carry a meeting quota, run a tight multi-channel cadence, and act as the bar-setter the junior SDRs watch. As one of the early sales hires in India you also help shape the playbook itself.',
    whatYoullDo: [
      'Hit 12 sales-qualified opportunities a month for the India and APAC AEs',
      'Own a named territory end-to-end: research, sequencing, cold calls and social',
      'Use the Idukki theme detector to qualify a brand’s current UGC stack before you reach out',
      'Run messaging experiments and feed the winners back into the team’s templates',
      'Mentor junior SDRs: review their sequences, sit on their call reviews, set the standard',
    ],
    whatYoullNeed: [
      '3-6 years of SDR or BDR experience, ideally selling SaaS into ecommerce or martech',
      'A track record of beating a meetings quota you can talk through honestly',
      'Fluent, persuasive written English and a confident phone presence',
      'Apollo / Outreach / SalesLoft fluency and clean CRM discipline',
    ],
    niceToHaves: [
      'Sold into ecommerce, retail or DTC brands before',
      'Early-team experience where you helped build process, not just follow it',
    ],
    compensationDetails: [
      'Base: ₹14.5L – ₹17.5L depending on experience',
      'OTE: 1.5× base, tied to qualified-opportunity progression',
      'Real ownership of a territory and a documented path to Account Executive',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  {
    slug: 'senior-sdr-us-market',
    title: 'Senior Sales Development Rep. US Market',
    team: 'Sales',
    level: 'Senior',
    location: 'Remote (India), US business hours',
    type: 'Full-time',
    compensation: '₹16.5L – ₹21L + 1.5× OTE',
    summary: 'India-based, selling into the US. Run outbound to American DTC and Shopify Plus brands on US hours, with a night-shift premium and a front-row view of our biggest market.',
    highlights: ['3-6 yrs SDR experience', 'US territory, India-based', 'US-hours shift + premium', '12 SQOs / month'],
    about: 'The US is our biggest growth market and we sell into it from India. You work American business hours (with the shift premium that comes with it), prospect into US DTC and Shopify Plus retail, and book meetings for the AMER account executives. It suits a senior SDR who already knows how to sell to a US buyer: the vocabulary, the pace, the objections. You are the tip of the spear for the market that matters most to us.',
    whatYoullDo: [
      'Hit 12 sales-qualified opportunities a month for the AMER AEs',
      'Prospect US DTC and Shopify Plus brands across East and West coast territories',
      'Run a US-tuned outbound motion: cold call, email and LinkedIn calibrated to an American buyer',
      'Use the Idukki theme detector to spot which US brands run Bazaarvoice, Yotpo or Stamped today',
      'Partner with the AMER AEs on a clean discovery handoff and feed objections back to growth',
    ],
    whatYoullNeed: [
      '3-6 years of SDR or BDR experience with real time selling into the US market',
      'Willing and able to work US business hours from India consistently',
      'Native-sounding, persuasive written and spoken English calibrated for a US audience',
      'A meetings-quota track record and fluency in Apollo / Outreach / SalesLoft',
    ],
    niceToHaves: [
      'Sold into US ecommerce, retail or martech specifically',
      'Comfortable running cold calls with US buyers and proud of a recording you can share',
    ],
    compensationDetails: [
      'Base: ₹16.5L – ₹21L depending on experience, including a US-hours shift premium',
      'OTE: 1.5× base, tied to qualified-opportunity progression',
      'A direct line into our largest market and a documented path to AMER Account Executive',
    ],
    interviewProcess: COMMON_INTERVIEW,
  },
  // ── ICP advisory roles ────────────────────────────────────────────────────
  // These fractional positions are designed to start conversations with
  // practitioners who are also Idukki's product ICP. They're real roles —
  // paid, structured, with genuine roadmap input — but the commitment is
  // 3-4 hours a month, not a full-time job.
  {
    slug: 'fractional-cro-advisor',
    title: 'Fractional CRO Advisor',
    team: 'Partnerships',
    level: 'Staff',
    location: 'Remote (global)',
    type: 'Contract',
    compensation: '£300–£600/mo retainer',
    summary: 'You optimise conversion for Shopify and DTC brands. We build the UGC and shoppable video platform brands depend on. This is the part where those two things actually talk to each other.',
    highlights: ['3–4 hrs/month', 'Paid retainer', 'Early feature access', 'Direct line to product'],
    about: "Idukki's widget lives on product pages, collection pages, and checkout flows for Shopify and DTC brands. It shows social proof, runs shoppable video, and fires events to analytics stacks. We build it; you know whether it actually moves conversion needles on the stores where you work.\n\nThis advisory role is 3–4 hours a month: a standing call with the product team, early access to unreleased features, and an honest view on what's worth building next. No full-time commitment, no long onboarding — just a structured way to have the conversation that should happen between a platform and its practitioner.",
    whatYoullDo: [
      'Monthly 60-min call with Rohin and the product team — roadmap input, feature feedback, what works and what does not',
      'Early access to unreleased features on your live clients, with a feedback loop back to the team',
      'Keep us honest on conversion mechanics — push back when a feature does not match how brands actually optimise',
      'Optional: a byline in our practitioner content series if you are open to writing about what you see',
    ],
    whatYoullNeed: [
      'Active CRO practice on Shopify, Shopify Plus, or comparable DTC storefronts',
      'Hands-on with A/B testing and UX metrics on product and collection pages',
      'An opinionated view on what social proof does and does not move the needle on',
    ],
    niceToHaves: [
      'Experience with shoppable video, UGC galleries, or social feeds on e-com',
      'View on how AI-curated content performs vs editorial curation',
    ],
    compensationDetails: [
      '£300–£600/month retainer depending on time commitment',
      'Rolling monthly agreement, cancel with 2 weeks notice',
      'Early access to Idukki Pro features for your client accounts',
    ],
    interviewProcess: [
      '30-min intro call with Rohin — no prep, just a conversation',
      'If there is a fit: a paid 3-month advisory agreement, flexible to your schedule',
      'Rolling monthly after that',
    ],
  },
  {
    slug: 'fractional-ugc-social-commerce-advisor',
    title: 'Fractional UGC & Social Commerce Advisor',
    team: 'Marketing',
    level: 'Lead',
    location: 'Remote (global)',
    type: 'Contract',
    compensation: '£200–£500/mo retainer',
    summary: 'You source UGC, manage rights, and run shoppable social campaigns for DTC brands. We want to be the platform practitioners actually recommend. This is how we get there.',
    highlights: ['3–4 hrs/month', 'Paid retainer', 'Beta feature access', 'Direct product input'],
    about: "At Idukki, we collect UGC from Instagram, TikTok, YouTube and a dozen other platforms, rights-manage it, and display it in shoppable galleries for brands across the web. The people who know whether we're getting that right are the ones doing it at scale for real brands.\n\nThis role is 3–4 hours a month of structured conversation: roadmap calls, feature betas, and an unfiltered view on the gaps between what we build and what practitioners actually need. Flexible, paid, no long-term lock-in.",
    whatYoullDo: [
      'Monthly call with the product team: what is working in your UGC stack, what is not, what you wish Idukki did differently',
      'Beta test new collection, rights-management, and syndication features before they ship',
      'Help calibrate what good looks like: metrics, moderation decisions, creator communication workflows',
      'Optional input on practitioner content — guides, benchmarks, or a joint piece if there is a topic worth writing',
    ],
    whatYoullNeed: [
      'Active UGC management practice on at least one Shopify or DTC brand',
      'Hands-on with rights requests, creator outreach, or content moderation at scale',
      'A clear view on what platform features would make your job faster or less painful',
    ],
    niceToHaves: [
      'Agency-side experience managing UGC across multiple brands',
      'Has evaluated or used platforms like Bazaarvoice, Yotpo, Flowbox, Nosto, or Idukki itself',
      'Familiar with differences between Instagram, TikTok, and YouTube UGC in a commerce context',
    ],
    compensationDetails: [
      '£200–£500/month retainer depending on time',
      'Rolling monthly, cancel with 2 weeks notice',
      'Idukki Pro access for your client accounts',
    ],
    interviewProcess: [
      '30-min intro call with Rohin — no prep needed, just a conversation about what you are working on',
      'If there is a fit: a paid 3-month advisory agreement on your schedule',
      'Rolling monthly after that',
    ],
  },
]

export function findRole(slug: string): Role | undefined {
  return roles.find((r) => r.slug === slug)
}

// Visual palette per team, used by the careers cards + role detail
// page. Tints are deliberately distinct so the index reads as a
// "departments" grid at a glance instead of a wall of grey cards.
// Each tile gets a gradient hero, an accent ring colour, and a
// suggested Unsplash photo that hints at the team's day-to-day work.
export type TeamVisual = {
  /** Tailwind gradient classes for the card hero. */
  gradient: string
  /** Tailwind colour class for the icon glyph. */
  iconColor: string
  /** Tailwind bg class for the icon tile. */
  iconBg: string
  /** Hint label shown bottom-left of the card hero. */
  hint: string
  /** Unsplash photo ID for the background. */
  imageId: string
}

export const teamVisuals: Record<RoleTeam, TeamVisual> = {
  Engineering: {
    gradient: 'from-indigo-500/15 via-indigo-500/5 to-transparent',
    iconColor: 'text-indigo-600 dark:text-indigo-300',
    iconBg: 'bg-indigo-500/12 ring-1 ring-indigo-500/20',
    hint: 'Ship every Friday',
    imageId: 'photo-1517694712202-14dd9538aa97',
  },
  'AI/ML': {
    gradient: 'from-fuchsia-500/15 via-fuchsia-500/5 to-transparent',
    iconColor: 'text-fuchsia-600 dark:text-fuchsia-300',
    iconBg: 'bg-fuchsia-500/12 ring-1 ring-fuchsia-500/20',
    hint: 'Models in production',
    imageId: 'photo-1677442136019-21780ecad995',
  },
  Design: {
    gradient: 'from-rose-500/15 via-rose-500/5 to-transparent',
    iconColor: 'text-rose-600 dark:text-rose-300',
    iconBg: 'bg-rose-500/12 ring-1 ring-rose-500/20',
    hint: 'Figma → prod in days',
    imageId: 'photo-1561070791-2526d30994b8',
  },
  Sales: {
    gradient: 'from-emerald-500/15 via-emerald-500/5 to-transparent',
    iconColor: 'text-emerald-700 dark:text-emerald-300',
    iconBg: 'bg-emerald-500/12 ring-1 ring-emerald-500/25',
    hint: 'Real quota, real comp',
    imageId: 'photo-1556761175-5973dc0f32e7',
  },
  'Customer Success': {
    gradient: 'from-amber-500/15 via-amber-500/5 to-transparent',
    iconColor: 'text-amber-700 dark:text-amber-300',
    iconBg: 'bg-amber-500/15 ring-1 ring-amber-500/25',
    hint: '118% NRR target',
    imageId: 'photo-1573497019418-b400bb3ab074',
  },
  Marketing: {
    gradient: 'from-sky-500/15 via-sky-500/5 to-transparent',
    iconColor: 'text-sky-700 dark:text-sky-300',
    iconBg: 'bg-sky-500/12 ring-1 ring-sky-500/25',
    hint: 'Original data > LLM slop',
    imageId: 'photo-1432888622747-4eb9a8efeb07',
  },
  Partnerships: {
    gradient: 'from-violet-500/15 via-violet-500/5 to-transparent',
    iconColor: 'text-violet-600 dark:text-violet-300',
    iconBg: 'bg-violet-500/12 ring-1 ring-violet-500/25',
    hint: 'Owns the agency P&L',
    imageId: 'photo-1521737604893-d14cc237f11d',
  },
  Operations: {
    gradient: 'from-teal-500/15 via-teal-500/5 to-transparent',
    iconColor: 'text-teal-700 dark:text-teal-300',
    iconBg: 'bg-teal-500/12 ring-1 ring-teal-500/25',
    hint: 'Keeps the founder unblocked',
    imageId: 'photo-1497215728101-856f4ea42174',
  },
}
